import { useState } from 'react'
import { useCreateApiKey } from '@/api/hooks/use-api-keys'
import { CopyButton } from '@/components/ui/copy-button'
import { Modal } from '@/components/ui/modal'

type KeyScope = 'send' | 'full'
type KeyType = 'live' | 'test'

interface CreateKeyModalProps {
  onClose: () => void
}

export function CreateKeyModal({ onClose }: CreateKeyModalProps): React.JSX.Element {
  const createKey = useCreateApiKey()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<KeyScope>('full')
  const [type, setType] = useState<KeyType>('live')
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    createKey.mutate({ name, scope, type }, { onSuccess: (res) => setCreatedKey(res.key) })
  }

  if (createdKey) {
    return (
      <Modal open onClose={onClose} title="API Key Created">
        <div className="space-y-4">
          <div className="rounded-lg border border-warning/30 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-800">
              Copy this key now. You will not be able to see it again.
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-surface-1 p-3">
            <code className="flex-1 break-all font-mono text-sm select-all">{createdKey}</code>
            <CopyButton value={createdKey} />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} title="Create API Key">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="key-name" className="block text-sm font-medium text-text-primary">
            Name
          </label>
          <input
            id="key-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Production, CI Pipeline"
            className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-text-primary">Scope</span>
          <div className="mt-1 flex gap-3">
            <ScopeOption
              value="send"
              selected={scope}
              onSelect={setScope}
              label="Send only"
              description="Can only send emails"
            />
            <ScopeOption
              value="full"
              selected={scope}
              onSelect={setScope}
              label="Full access"
              description="All API endpoints"
            />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-text-primary">Type</span>
          <div className="mt-1 flex gap-3">
            <TypeOption value="live" selected={type} onSelect={setType} label="Live" />
            <TypeOption value="test" selected={type} onSelect={setType} label="Test" />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || createKey.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {createKey.isPending ? 'Creating...' : 'Create Key'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function ScopeOption({
  value,
  selected,
  onSelect,
  label,
  description,
}: {
  value: KeyScope
  selected: KeyScope
  onSelect: (v: KeyScope) => void
  label: string
  description: string
}): React.JSX.Element {
  const isSelected = value === selected
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`flex-1 rounded-lg border p-3 text-left text-sm ${isSelected ? 'border-brand-500 bg-brand-50' : 'border-surface-3 hover:bg-surface-1'}`}
    >
      <span className="font-medium">{label}</span>
      <p className="mt-0.5 text-xs text-text-secondary">{description}</p>
    </button>
  )
}

function TypeOption({
  value,
  selected,
  onSelect,
  label,
}: {
  value: KeyType
  selected: KeyType
  onSelect: (v: KeyType) => void
  label: string
}): React.JSX.Element {
  const isSelected = value === selected
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium ${isSelected ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-surface-3 text-text-secondary hover:bg-surface-1'}`}
    >
      {label}
    </button>
  )
}
